<template>
  <span v-if="props.anchor" :id="props.anchor" class="anchor"></span>
  <div class="card" v-bind="$attrs">
    <div class="card-header d-flex justify-content-between bg-primary">
      <h4 class="text-uppercase mb-0">Information supplémentaires</h4>
      <button class="btn btn-link link-dark p-0 link-underline-opacity-0 link-underline-opacity-100-hover" @click="addInfo()"><i class="fa-solid fa-plus pe-1"></i>Ajouter une information</button>
    </div>
        <div class="card-body">
      <div v-if="infos.length === 0" class="text-muted">
        Aucune information supplémentaire
      </div>
      <div v-else v-for="(info,idx) in infos" :key="info.key" class="card mb-3">
        <span :id="`infoFields[${idx}]`" class="anchor"></span>
        <div class="card-header d-flex justify-content-between bg-primary bg-opacity-25">
          <h4 class="text-uppercase mb-0">{{info.value?.label.length > 0 ? info.value?.label : `Information ${idx + 1}`}}</h4>
          <button class="btn btn-link link-dark p-0" @click="removeInfo(idx)"><i class="fa-regular fa-trash-can fa-lg"></i></button>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-12 col-md-6">
              <TextInputComponent
                :value-key="`infoFields[${idx}].label`"
                label="Nom"
                placeholder="Nom"
                :required="true"
                class="mb-3"
              />
            </div>
            <div class="col-12 col-md-6">
              <SelectInputComponent
                :value-key="`infoFields[${idx}].type`"
                label="Type"
                placeholder="Selectionnez un type"
                :options="Object.values(TravelDbFieldTypeEnum).map(type => ({ value: type, label: GetInfoFieldTypeLabel(type) })).sort((a, b) => a.label.localeCompare(b.label))"
                :required="true"
                class="mb-3"
              />
            </div>
            <div class="col-12 col-md-6">
              <CheckboxComponent
                :value-key="`infoFields[${idx}].required`"
                label="Requis ?"
              />
            </div>
            <div class="col-12 col-md-6">
              <CheckboxComponent
                :value-key="`infoFields[${idx}].alsoForCompanion`"
                label="Demander aussi aux accompagnants ?"
              />
            </div>
            <div class="col-12 col-md-6" v-if="info.value.type === TravelDbFieldTypeEnum.Choice">
              <TagInputComponent
                :value-key="`infoFields[${idx}].choices`"
                label="Liste des choix possibles"
                placeholder="Liste des choix possibles"
                :required="true"
                :tag-validator="tagValidator"
                class="mt-3 mt-md-0"
              />
            </div>

          </div>
        </div>

      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useFieldArray } from 'vee-validate';
import TextInputComponent from '@/components/common/TextInputComponent.vue';
import SelectInputComponent from '@/components/common/SelectInputComponent.vue';
import { GetInfoFieldTypeLabel, IdNotDefined, ScrollToId } from '@/compositions/helper';
import * as zod from 'zod';

const validationSchema = defineModel<{ shape: zod.ZodRawShape, anchors?: Record<string, string | undefined> }>('validation-schema',{ required: false, default: () => ({ shape: {} }) });

const props = defineProps<{
  anchor?: string
}>();

const { remove: removeInfo, push: pushInfo, fields: infos } = useFieldArray<TravelDbInfoField>(() => 'infoFields');

const defaultInfo = <TravelDbInfoField>{
  id: IdNotDefined,
  type: TravelDbFieldTypeEnum.Text,
  label: '',
  value: '',
  choices: [],
  required: false,
  alsoForCompanion: false
}

const newInfo = ref<TravelDbInfoField>(Object.assign({}, defaultInfo));

const tagValidator = (tag: string, choices?: string[]) => {
  const res = ((choices?.map(choice => choice.toLowerCase())?.indexOf(tag.toLowerCase())) ?? -1) === -1;
  return res;
}

const addInfo = () => {
  pushInfo(newInfo.value);
  newInfo.value = Object.assign({}, defaultInfo);
  ScrollToId(`infoFields[${(infos?.value.length ?? 1) - 1}]`);
}

onMounted(() => {
  validationSchema.value = {
    shape:{
      infoFields: zod.array(zod.object({
        id: zod.number().nonnegative(),
        type: zod.nativeEnum(TravelDbFieldTypeEnum, { message: 'Le type d\'information est requis' }),
        label: zod.string().min(1, 'Le nom du champs est obligatoire'),
        required: zod.boolean(),
        alsoForCompanion: zod.boolean(),
        choices: zod.array(zod.string()).optional()
      })
      .refine((data) => {
        if (data.type === TravelDbFieldTypeEnum.Choice && (!data.choices || data.choices.length === 0)) {
          return false;
        }
        return true;
      }, { message: 'Vous devez ajouter au moins un choix' })
    ).optional()
    },
    anchors: {
      "infoFields": props.anchor,
      "infoFields[\\d].id": "self",
      "infoFields[\\d].type": "self",
      "infoFields[\\d].label": "self",
      "infoFields[\\d].required": "self",
      "infoFields[\\d].alsoForCompanion": "self",
      "infoFields[\\d].choices": "self",
    }
  }
});
</script>
